import { Button, showSuccessToast } from '@bpinternal/ui-kit-next'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Plus } from 'lucide-react'
import { Boundary, Page } from '~/components'
import { EditableTextField } from '~/components/EditableTextField'
import { showAddKnowledgeSourceDialog } from '~/features/knowledge-bases/components/knowledge-source/AddKnowledgeSource/AddKnowledgeSourceDialog'
import { WebPageSourceTable } from '~/features/knowledge-bases/components/knowledge-source/WebPageSourceTable'
import { DropboxSourceTable } from '~/features/knowledge-bases/components/knowledge-source/DropboxSourceTable'
import { useKnowledgeBase } from '~/features/knowledge-bases/hooks/useKnowledgeBase'
import {
  useRenameWebPageSource,
  useWebPageSource,
  useDropboxSource,
  useRenameDropboxSource,
} from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { useSuspenseQuery } from '~/services'
import type { DSData } from '~/features/knowledge-bases/types'

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId/$dsId')({
  component: RouteComponent,
})

function RouteComponent() {
  const { workspaceId, botId, kbId, dsId } = Route.useParams()
  const { data: kb } = useKnowledgeBase({ workspaceId, botId, kbId })

  // Determine the source type from dsData
  const { data: stateData } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })
  const dsData: { [key: string]: DSData } = stateData.state?.payload?.[kb.oldKbId ?? kbId] || {}
  const sourceData = dsData[dsId]
  const isDropbox = sourceData?.type === 'dropbox'

  const { title: webPageTitle } = useWebPageSource({ workspaceId, botId, kbId: kb.oldKbId ?? kbId, dsId })
  const { title: dropboxTitle } = useDropboxSource({ workspaceId, botId, kbId: kb.oldKbId ?? kbId, dsId })
  const title = isDropbox ? dropboxTitle : webPageTitle

  const {
    mutate: renameWebPageSource,
    isPending: isPendingWebPage,
    isSuccess: isSuccessWebPage,
  } = useRenameWebPageSource({ workspaceId, botId })
  const {
    mutate: renameDropboxSource,
    isPending: isPendingDropbox,
    isSuccess: isSuccessDropbox,
  } = useRenameDropboxSource({ workspaceId, botId })

  const isPending = isDropbox ? isPendingDropbox : isPendingWebPage
  const isSuccess = isDropbox ? isSuccessDropbox : isSuccessWebPage

  const handleRename = (newName: string) => {
    if (isDropbox) {
      renameDropboxSource(
        { workspaceId, botId, kbId: kb.oldKbId ?? kbId, dsId, name: newName },
        {
          onSuccess: () => {
            showSuccessToast(`Dropbox source has been successfully renamed to "${newName}"`)
          },
        }
      )
    } else {
      renameWebPageSource(
        { workspaceId, botId, kbId: kb.oldKbId ?? kbId, dsId, name: newName },
        {
          onSuccess: () => {
            showSuccessToast(`Web page source has been successfully renamed to "${newName}"`)
          },
        }
      )
    }
  }

  return (
    <Page
      primaryActions={
        !isDropbox ? (
          <Button
            className="h-full w-full md:w-fit"
            onClick={(e) => {
              e.stopPropagation()

              showAddKnowledgeSourceDialog({
                workspaceId,
                botId,
                kbId: kb.oldKbId ?? kbId,
                dsId,
                startStep: 'sync_website',
                hideBackArrow: true,
              })
            }}
          >
            <Plus />
            Add Pages
          </Button>
        ) : null
      }
      title={
        <div className="flex w-full gap-2">
          <Link to="/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId" params={{ workspaceId, botId, kbId }}>
            <Button variant="ghost" size="icon">
              <ArrowLeft />
            </Button>
          </Link>
          <EditableTextField
            defaultName={title}
            onEdit={handleRename}
            isPending={isPending}
            isSuccess={isSuccess}
            maxLength={64}
            showError
          />
        </div>
      }
    >
      <Boundary>
        {isDropbox ? (
          <DropboxSourceTable
            workspaceId={workspaceId}
            botId={botId}
            kbId={kb.oldKbId ?? kbId}
            dsId={dsId}
            knowledgeBaseName={kb.name}
          />
        ) : (
          <WebPageSourceTable
            workspaceId={workspaceId}
            botId={botId}
            kbId={kb.oldKbId ?? kbId}
            dsId={dsId}
            knowledgeBaseName={kb.name}
          />
        )}
      </Boundary>
    </Page>
  )
}
